import { z } from 'zod';
import type { EffectivePermissions } from './permissions';

/**
 * Catalogue (cahier des charges §6) : section → famille → (type/puissance) → marque/variante →
 * article → tarif. Tout est administrable ; AUCUN prix ni barème n'est codé ou préchargé.
 *
 * Les montants sont en centimes (entiers) ; les taux en points de base (1 % = 100) pour éviter
 * toute erreur d'arrondi flottant.
 */

const uuid = z
  .string()
  .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, 'UUID invalide');
const label = z.string().trim().min(1, 'Libellé obligatoire').max(200);

export const SECTION_KINDS = ['equipment', 'service', 'accessory', 'option'] as const;
export type SectionKind = (typeof SECTION_KINDS)[number];
export const SECTION_KIND_LABELS: Record<SectionKind, string> = {
  equipment: 'Équipements (installation)',
  service: 'Prestations (entretien…)',
  accessory: 'Accessoires & suppléments',
  option: 'Options',
};

export const UNITS = ['unit', 'meter', 'piece', 'flat'] as const;
export type CatalogUnit = (typeof UNITS)[number];
export const UNIT_LABELS: Record<CatalogUnit, string> = {
  unit: 'unité',
  meter: 'mètre',
  piece: 'pièce',
  flat: 'forfait',
};

/**
 * Article ajouté AUTOMATIQUEMENT au dossier selon ses conditions d'intervention (et retiré si elles changent) :
 * nacelle jusqu'à 8 m / au-dessus de 8 m. Prix et libellé viennent du catalogue (administrables).
 */
export const AUTO_RULES = ['nacelle_up_to_8m', 'nacelle_over_8m'] as const;
export type AutoRule = (typeof AUTO_RULES)[number];
export const AUTO_RULE_LABELS: Record<AutoRule, string> = {
  nacelle_up_to_8m: 'Nacelle choisie, hauteur jusqu’à 8 m',
  nacelle_over_8m: 'Nacelle choisie, hauteur au-dessus de 8 m',
};
/** Règle automatique qui s'applique au dossier (null = aucune). */
export function autoRuleFor(d: {
  installAtHeight?: boolean | null;
  installAccess?: string | null;
  installHeightM?: number | null;
}): AutoRule | null {
  if (!d.installAtHeight || d.installAccess !== 'nacelle') return null;
  return d.installHeightM != null && d.installHeightM > 8 ? 'nacelle_over_8m' : 'nacelle_up_to_8m';
}

/** Classes énergétiques proposées (étiquette européenne des climatiseurs) */
export const ENERGY_CLASSES = ['A+++', 'A++', 'A+', 'A', 'B', 'C', 'D'] as const;
export type EnergyClass = (typeof ENERGY_CLASSES)[number];

export const sectionInput = z.object({
  code: z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z0-9_]{2,50}$/),
  label,
  kind: z.enum(SECTION_KINDS),
  sortOrder: z.number().int().default(0),
});
export const familyInput = z.object({ sectionId: uuid, label, sortOrder: z.number().int().default(0) });
export const brandInput = z.object({ label, sortOrder: z.number().int().default(0) });

export const itemInput = z
  .object({
    sectionId: uuid,
    familyId: uuid.nullish().transform((v) => v ?? null),
    brandId: uuid.nullish().transform((v) => v ?? null),
    label,
    /** variante / modèle */
    model: z
      .string()
      .trim()
      .max(200)
      .nullish()
      .transform((v) => v || null),
    description: z
      .string()
      .trim()
      .max(4000)
      .nullish()
      .transform((v) => v || null),
    unit: z.enum(UNITS).default('unit'),
    /** puissance (BTU) : égale pour un appareil, tranche min–max pour un entretien */
    powerMinBtu: z
      .number()
      .int()
      .min(0)
      .max(200_000)
      .nullish()
      .transform((v) => v ?? null),
    powerMaxBtu: z
      .number()
      .int()
      .min(0)
      .max(200_000)
      .nullish()
      .transform((v) => v ?? null),
    /** types de dossier concernés ; vide = tous */
    dossierTypeIds: z.array(uuid).default([]),
    /** territoires ; vide = tous */
    territoryIds: z.array(uuid).default([]),
    /** règle de stock (module Stock) : stocké ou illimité */
    stockMode: z.enum(['stocked', 'unlimited']).default('unlimited'),
    /** étapes affichées dépliées (ex. traitement anticorrosion), reprises au récapitulatif et au contrat */
    optionSteps: z.array(z.string().trim().min(1).max(300)).max(30).default([]),
    /** classe énergétique de la variante (A++, A+++…) */
    energyClass: z
      .enum(ENERGY_CLASSES)
      .nullish()
      .transform((v) => v ?? null),
    /** garantie propre à la variante / au modèle, telle que fournie (jamais déduite) */
    /** articles dont l'un DOIT figurer dans le même dossier (ex. extension de garantie → contrat d'entretien 5 ans) */
    requiresItemIds: z.array(uuid).max(10).default([]),
    /** mis en avant dans un encadré du devis / bon de commande (ex. extension de garantie) */
    quoteHighlight: z.boolean().default(false),
    /** ajout automatique au dossier (nacelle…), null = jamais */
    autoRule: z
      .enum(AUTO_RULES)
      .nullish()
      .transform((v) => v ?? null),
    warranty: z
      .string()
      .trim()
      .max(300)
      .nullish()
      .transform((v) => v || null),
    sortOrder: z.number().int().default(0),
  })
  .refine((v) => v.powerMinBtu === null || v.powerMaxBtu === null || v.powerMinBtu <= v.powerMaxBtu, {
    message: 'Puissance min > max',
    path: ['powerMaxBtu'],
  });
export type ItemInput = z.input<typeof itemInput>;

/** Une version de tarif (append-only, jamais modifiée : on en ajoute une nouvelle). */
export const priceInput = z
  .object({
    /** null = tous territoires (valeur par défaut) */
    territoryId: uuid.nullish().transform((v) => v ?? null),
    priceHtCents: z.number().int().min(0).max(100_000_000),
    /** points de base : 850 = 8,5 % */
    vatRateBp: z.number().int().min(0).max(10_000),
    saleCommissionType: z.enum(['none', 'fixed', 'percent']).default('none'),
    /** centimes par unité (fixed) ou points de base du HT (percent) */
    saleCommissionValue: z.number().int().min(0).max(100_000_000).default(0),
    /** rémunération de pose / d'entretien, centimes par unité */
    installRemunerationCents: z
      .number()
      .int()
      .min(0)
      .max(100_000_000)
      .nullish()
      .transform((v) => v ?? null),
    /** ancien prix HT, affiché barré dans le catalogue (facultatif) */
    /** prix TTC exact (ce que le client paie) : s'il est donné, le HT en est déduit et le TTC ne bouge plus d'un centime */
    priceTtcCents: z
      .number()
      .int()
      .min(0)
      .max(100_000_000)
      .nullish()
      .transform((v) => v ?? null),
    /** ancien prix TTC exact affiché barré (facultatif) */
    compareAtPriceTtcCents: z
      .number()
      .int()
      .min(0)
      .max(100_000_000)
      .nullish()
      .transform((v) => v ?? null),
    compareAtPriceHtCents: z
      .number()
      .int()
      .min(0)
      .max(100_000_000)
      .nullish()
      .transform((v) => v ?? null),
    /** date d'effet (défaut : maintenant) ; une date future prépare un changement de tarif */
    validFrom: z.string().datetime().optional(),
    note: z
      .string()
      .trim()
      .max(500)
      .nullish()
      .transform((v) => v || null),
  })
  .refine((v) => v.saleCommissionType !== 'percent' || v.saleCommissionValue <= 10_000, {
    message: 'Pourcentage > 100 %',
    path: ['saleCommissionValue'],
  });
export type PriceInput = z.input<typeof priceInput>;

export interface PriceVersion {
  id: string;
  territoryId: string | null;
  validFrom: string;
  priceHtCents: number;
  vatRateBp: number;
  /** masqués (null) pour qui n'a pas le droit de les voir */
  saleCommissionType: 'none' | 'fixed' | 'percent' | null;
  saleCommissionValue: number | null;
  installRemunerationCents: number | null;
  /** ancien prix HT affiché barré (null = aucun) */
  compareAtPriceHtCents: number | null;
  /** prix et ancien prix TTC exacts tels que saisis (null = déduits du HT) */
  priceTtcCents: number | null;
  compareAtPriceTtcCents: number | null;
}

/** HT déduit d'un TTC exact (arrondi au centime ; le TTC fait foi). */
export const htOfTtc = (ttc: number, vatRateBp: number) => Math.round((ttc * 10_000) / (10_000 + vatRateBp));
/** Prix TTC affiché d'une version de tarif : le TTC saisi s'il existe, sinon calculé depuis le HT. */
export const priceTtcOf = (p: Pick<PriceVersion, 'priceHtCents' | 'priceTtcCents' | 'vatRateBp'>) =>
  p.priceTtcCents ?? ttcCents(p.priceHtCents, p.vatRateBp);
export const compareAtTtcOf = (
  p: Pick<PriceVersion, 'compareAtPriceHtCents' | 'compareAtPriceTtcCents' | 'vatRateBp'>,
) =>
  p.compareAtPriceTtcCents ??
  (p.compareAtPriceHtCents != null ? ttcCents(p.compareAtPriceHtCents, p.vatRateBp) : null);

/**
 * Tarif applicable à un article pour un territoire à une date : la version la plus récente déjà
 * en vigueur, spécifique au territoire en priorité, sinon la version « tous territoires ».
 */
export function currentPrice<P extends Pick<PriceVersion, 'territoryId' | 'validFrom'>>(
  prices: readonly P[],
  territoryId: string,
  at: Date = new Date(),
): P | undefined {
  const inForce = prices.filter((p) => new Date(p.validFrom) <= at);
  const latest = (list: P[]) => list.sort((a, b) => b.validFrom.localeCompare(a.validFrom))[0];
  return (
    latest(inForce.filter((p) => p.territoryId === territoryId)) ??
    latest(inForce.filter((p) => p.territoryId === null))
  );
}

/* ------------------------------------------------------------------ désignation d'une ligne (devis) */

export interface ItemForLine {
  label: string;
  model: string | null;
  brandId: string | null;
  energyClass?: string | null;
  warranty?: string | null;
  optionSteps: string[];
  quoteHighlight?: boolean;
}

/** Variante d'appareil (marque renseignée) : marque, modèle, classe énergétique et garantie sont obligatoires au devis. */
export function missingForSale(
  item: Pick<ItemForLine, 'brandId' | 'model' | 'energyClass' | 'warranty'>,
): string[] {
  if (!item.brandId) return [];
  return [
    !item.model && 'modèle / référence',
    !item.energyClass && 'classe énergétique',
    !item.warranty && 'garantie',
  ].filter((x): x is string => !!x);
}

/**
 * Ce que la ligne fige au moment de la vente, composé depuis la VARIANTE réellement choisie (jamais écrit en dur) :
 * « Climatiseur 12 000 BTU – Midea – XXXX – A+++ », puis le détail de la prestation et la garantie du modèle.
 * Même calcul sur l'appareil (hors ligne) et sur le serveur.
 */
export function lineSnapshot(item: ItemForLine, brandLabel: string | null | undefined) {
  const quoteHighlight = !!item.quoteHighlight;
  if (!item.brandId) return { label: item.label, model: item.model, optionSteps: item.optionSteps, quoteHighlight };
  const label = [item.label, brandLabel, item.model, item.energyClass].filter(Boolean).join(' – ');
  const optionSteps = [...item.optionSteps, ...(item.warranty ? [`Garantie : ${item.warranty}`] : [])];
  return { label, model: null, optionSteps, quoteHighlight };
}

/** Prix TTC d'un prix HT (centimes, arrondi comme une ligne de quantité 1). */
export const ttcCents = (htCents: number, vatRateBp: number) =>
  htCents + Math.round((htCents * vatRateBp) / 10_000);

/* ------------------------------------------------------------------ lignes et totaux */

/**
 * Remise accordée au client sur une ligne, au moment du choix du produit :
 *  - « commerciale » : consentie par le commercial, son montant est DÉDUIT DE SA COMMISSION ;
 *  - « accord dirigeant / responsable » : prise en charge par l'entreprise, l'administrateur en est averti.
 */
export const DISCOUNT_KINDS = ['commercial', 'management'] as const;
export type DiscountKind = (typeof DISCOUNT_KINDS)[number];
export const DISCOUNT_KIND_LABELS: Record<DiscountKind, string> = {
  commercial: 'Remise commerciale (déduite de ma commission)',
  management: 'Accord dirigeant / responsable',
};

/** Règles d'une remise : un montant exige son type (et inversement), jamais plus que la ligne elle-même. */
export function discountErrors(l: {
  discountHtCents: number;
  discountKind: DiscountKind | null;
  quantity: number;
  unitPriceHtCents: number;
}): Record<string, string> {
  const e: Record<string, string> = {};
  if (l.discountHtCents > 0 && !l.discountKind)
    e.discountKind = 'Choisissez : remise commerciale ou accord dirigeant';
  if (l.discountKind && l.discountHtCents <= 0) e.discountHtCents = 'Montant de la remise obligatoire';
  if (l.discountHtCents > Math.round(l.unitPriceHtCents * l.quantity))
    e.discountHtCents = 'Remise supérieure au prix de la ligne';
  return e;
}

export const lineCreateInput = z.object({
  id: uuid,
  dossierId: uuid,
  itemId: uuid,
  quantity: z.number().positive('Quantité > 0').max(100_000),
  notes: z
    .string()
    .trim()
    .max(1000)
    .nullish()
    .transform((v) => v || null),
  /** montant HT de la remise sur la ligne (quantité comprise), en centimes ; 0 = aucune */
  discountHtCents: z.number().int().min(0).max(100_000_000).default(0),
  discountKind: z
    .enum(DISCOUNT_KINDS)
    .nullish()
    .transform((v) => v ?? null),
  /** facultatif : qui a donné l'accord, motif… */
  discountNote: z
    .string()
    .trim()
    .max(300)
    .nullish()
    .transform((v) => v || null),
  /** remise saisie en TTC (exacte) */
  discountTtcCents: z.number().int().min(0).max(100_000_000).nullish(),
  /** administrateur : prix TTC exact propre à ce dossier */
  unitPriceTtcCents: z.number().int().min(0).max(100_000_000).nullish(),
  /** administrateur : ancien prix TTC exact barré */
  compareAtPriceTtcCents: z.number().int().min(0).max(100_000_000).nullish(),
  /** administrateur (dossier.price.override) : prix HT propre à CE dossier ; ignoré sans ce droit */
  unitPriceHtCents: z.number().int().min(0).max(100_000_000).nullish(),
  /** administrateur : ancien prix HT affiché barré sur ce dossier (facultatif) */
  compareAtPriceHtCents: z
    .number()
    .int()
    .min(0)
    .max(100_000_000)
    .nullish()
    .transform((v) => v ?? null),
});
export type LineCreateInput = z.input<typeof lineCreateInput>;
export const LINE_MUTABLE_FIELDS = [
  'quantity',
  'notes',
  'discountHtCents',
  'discountKind',
  'discountNote',
  'unitPriceHtCents',
  'compareAtPriceHtCents',
  'unitPriceTtcCents',
  'compareAtPriceTtcCents',
  'discountTtcCents',
] as const;
/** champs de prix d'une ligne réservés à l'administrateur (prix propre au dossier, ancien prix barré) */
export const LINE_PRICE_FIELDS = [
  'unitPriceHtCents',
  'compareAtPriceHtCents',
  'unitPriceTtcCents',
  'compareAtPriceTtcCents',
] as const;

/** HT (centimes) dont le TTC retombe exactement sur le montant saisi (ce que le client voit). */
export function htFromTtcCents(ttc: number, vatRateBp: number): number {
  const guess = Math.round((ttc * 10_000) / (10_000 + vatRateBp));
  for (const d of [0, -1, 1, -2, 2, -3, 3]) if (ttcCents(guess + d, vatRateBp) === ttc) return guess + d;
  return guess;
}
export const lineFieldsInput = z.object({
  quantity: z.number().positive('Quantité > 0').max(100_000),
  notes: z.string().trim().max(1000).nullable(),
  discountHtCents: z.number().int().min(0).max(100_000_000),
  discountKind: z.enum(DISCOUNT_KINDS).nullable(),
  discountNote: z.string().trim().max(300).nullable(),
  unitPriceHtCents: z.number().int().min(0).max(100_000_000),
  compareAtPriceHtCents: z.number().int().min(0).max(100_000_000).nullable(),
  unitPriceTtcCents: z.number().int().min(0).max(100_000_000).nullable(),
  compareAtPriceTtcCents: z.number().int().min(0).max(100_000_000).nullable(),
  discountTtcCents: z.number().int().min(0).max(100_000_000).nullable(),
});

export interface LineAmounts {
  quantity: number;
  unitPriceHtCents: number;
  vatRateBp: number;
  /** remise HT de la ligne (défaut 0) */
  discountHtCents?: number;
  /**
   * Prix unitaire TTC exact (saisi en TTC) : la ligne est alors calculée « TTC d'abord » — le TTC est exactement
   * prix × quantité − remise, le HT et la TVA en sont déduits. Jamais d'écart d'un centime sur ce que voit le client.
   */
  unitPriceTtcCents?: number | null;
  /** remise TTC exacte (lignes « TTC d'abord ») */
  discountTtcCents?: number | null;
}

/** Remise HT correspondant à un montant TTC saisi (ce que le client voit), arrondie au centime. */
export const discountHtFromTtc = (ttcCents: number, vatRateBp: number) =>
  Math.round((ttcCents * 10_000) / (10_000 + vatRateBp));

export function lineTotals(l: LineAmounts) {
  if (l.unitPriceTtcCents != null) {
    const discount = l.discountTtcCents ?? ttcCents(l.discountHtCents ?? 0, l.vatRateBp);
    const ttc = Math.round(l.unitPriceTtcCents * l.quantity) - discount;
    const ht = htOfTtc(ttc, l.vatRateBp);
    return { htCents: ht, vatCents: ttc - ht, ttcCents: ttc };
  }
  const htCents = Math.round(l.unitPriceHtCents * l.quantity) - (l.discountHtCents ?? 0);
  const vatCents = Math.round((htCents * l.vatRateBp) / 10_000);
  return { htCents, vatCents, ttcCents: htCents + vatCents };
}

/** Totaux du dossier : arrondi par ligne, puis somme (comme sur une facture). */
export function dossierTotals(lines: readonly LineAmounts[]) {
  return lines.reduce(
    (acc, l) => {
      const t = lineTotals(l);
      return {
        htCents: acc.htCents + t.htCents,
        vatCents: acc.vatCents + t.vatCents,
        ttcCents: acc.ttcCents + t.ttcCents,
      };
    },
    { htCents: 0, vatCents: 0, ttcCents: 0 },
  );
}

/** Commission de vente d'une ligne selon le barème en vigueur (figée à la validation du dossier). */
export function saleCommissionCents(
  rule: Pick<PriceVersion, 'saleCommissionType' | 'saleCommissionValue'>,
  line: LineAmounts,
): number {
  const value = rule.saleCommissionValue ?? 0;
  switch (rule.saleCommissionType) {
    case 'fixed':
      return Math.round(value * line.quantity);
    case 'percent':
      return Math.round((lineTotals(line).htCents * value) / 10_000);
    default:
      return 0;
  }
}

export function installRemunerationTotalCents(
  rule: Pick<PriceVersion, 'installRemunerationCents'>,
  quantity: number,
): number {
  return Math.round((rule.installRemunerationCents ?? 0) * quantity);
}

/**
 * Qui voit quels montants internes (CDC §11 : « les données de commission d'un utilisateur ne sont
 * jamais visibles par un autre utilisateur non autorisé » ; §20 minimisation) :
 *  - commissions de vente : barèmes administrés, ou vendeurs (commission.read + dossier.create) ;
 *  - rémunérations de pose/entretien : barèmes administrés, ou poseurs / planificateurs.
 * Le filtrage est fait par le serveur avant tout envoi.
 */
export function amountsVisibility(perms: EffectivePermissions) {
  const rules = !!perms['commission.rules.manage'];
  const read = !!perms['commission.read'];
  return {
    /** prix et totaux du client : pas pour le technicien, sauf s'il encaisse (case « paiements » cochée) */
    prices: !!perms['dossier.amounts.read'] || !!perms['payment.collect'],
    saleCommission: rules || (read && !!perms['dossier.create']),
    remuneration:
      rules ||
      (read &&
        (!!perms['intervention.execute'] || !!perms['planning.manage'] || !!perms['commission.manage'])),
  };
}

export function formatEuros(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(cents / 100);
}

export function formatRateBp(bp: number): string {
  return `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(bp / 100)} %`;
}

/**
 * Options liées : un article qui en EXIGE d'autres (ex. extension de garantie 5 ans → contrat d'entretien 5 ans)
 * n'est accepté que si l'un d'eux figure aussi au dossier. Renvoie un message par exigence non remplie.
 */
export function missingRequiredItems(
  lines: readonly { itemId: string; label: string }[],
  itemOf: (id: string) => { label: string; requiresItemIds?: readonly string[] } | undefined,
): string[] {
  const present = new Set(lines.map((l) => l.itemId));
  const out: string[] = [];
  for (const l of lines) {
    const req = itemOf(l.itemId)?.requiresItemIds ?? [];
    if (!req.length || req.some((id) => present.has(id))) continue;
    const names = req.map((id) => itemOf(id)?.label).filter(Boolean);
    out.push(`« ${l.label} » exige ${names.length ? `« ${names.join(' » ou « ')} »` : 'une autre option'} dans le même dossier`);
  }
  return [...new Set(out)];
}

/**
 * Changement de modèle d'une ligne (administrateur), possible même après la signature tant que la facture n'est pas
 * faite : prix, remise, quantité et commissions restent ceux signés ; motif obligatoire.
 */
export const lineSubstituteInput = z.object({
  itemId: z.string().uuid(),
  reason: z.string().trim().min(3, 'Motif obligatoire (ex. : rupture de stock du fournisseur)').max(500),
});
export type LineSubstituteInput = z.input<typeof lineSubstituteInput>;
